import { Card, Group, Stack, Text } from '@mantine/core';
import { IconEye, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { uniq } from 'lodash';
import { Show } from '../../../../react-commons/components/control-flow/Show';
import { Controlled_RichText } from '../../../../react-commons/components/inputs/controlled/Controlled_RichText';
import { TOOLTIPS } from '../../resource/tooltipTexts';
import { DisableableIconButtonWithTooltip } from './DisableableIconButtonWithTooltip';
import { LinkCopyButtonComponent } from './LinkCopyButtonComponent';
import { ResourceValidationTitleBar } from './validation/ResourceValidationTitleBar';

export const StackedCardsComponent = ({
    data,
    title,
    onEdit,
    onAdd,
    onDelete,
    onView,
    link,
    section,
}: {
    title: string;
    data: Array<{
        group?: string | null;
        id: string;
        text: string;
        img: string | null;
        description: string | null;
        roleId?: string | null;
    }>;
    onAdd?: () => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onView?: (id: string, roleId: string | undefined | null) => void;
    link: string | undefined;
    section: string;
}) => {
    const getGroupedData = () => {
        const groups = uniq(data.map((value) => value.group ?? null));
        const groupedData: Array<{
            group: string | null;
            values: Array<{
                group?: string | null;
                roleId?: string | null;
                id: string;
                text: string;
                img: string | null;
                description: string | null;
            }>;
        }> = [];

        groups.forEach((group) => {
            const filteredData = data.filter((value) => (value.group ?? null) === group);
            groupedData.push({ group, values: filteredData });
        });
        return groupedData;
    };

    return (
        <div
            style={{
                display: 'flex',
                overflowY: 'hidden',
                width: '100%',
                height: '100%',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    textAlign: 'center',
                    width: 'calc(100vw - 220px)',
                    height: 'calc(100% - 60px)',
                    overflowY: 'auto',
                    left: '220px',
                    padding: '10px',
                }}
            >
                <Stack>
                    <ResourceValidationTitleBar
                        customButtons={
                            <Group>
                                <Show when={link}>
                                    <LinkCopyButtonComponent link={link!} section={section} />
                                </Show>
                                {onAdd && (
                                    <DisableableIconButtonWithTooltip
                                        disabled={false}
                                        tooltipText={TOOLTIPS.CREATE_NEW_REFERENCE}
                                        icon={<IconPlus />}
                                        onClick={onAdd}
                                    />
                                )}
                            </Group>
                        }
                    >
                        <Text c={'dimmed'}>{title}</Text>
                    </ResourceValidationTitleBar>
                    {getGroupedData().map((group, idx) =>
                        group.group ?
                            <Card withBorder radius={'md'} key={idx}>
                                <Card.Section withBorder inheritPadding py="xs">
                                    <Stack align={'flex-start'} justify={'flex-start'} w={'100%'}>
                                        <Group w={'100%'}>
                                            <Text size={'lg'} fw={900}>{group.group}</Text>
                                            <div style={{ flexGrow: 1 }}></div>
                                        </Group>
                                        {group.values.map((element, index) => (
                                            <Card withBorder radius={'md'} key={index} w={'100%'}>
                                                <Card.Section withBorder inheritPadding py="xs">
                                                    <Stack align={'flex-start'} justify={'flex-start'} w={'100%'}>
                                                        <Group w={'100%'}>
                                                            {element.img && (
                                                                <img
                                                                    alt={element.img}
                                                                    src={element.img}
                                                                    style={{ height: '30px' }}
                                                                />
                                                            )}
                                                            <Text size={'lg'}>{element.text}</Text>
                                                            <div style={{ flexGrow: 1 }}></div>
                                                            <Group justify={'flex-end'}>
                                                                <Show when={onView}>
                                                                    <DisableableIconButtonWithTooltip
                                                                        disabled={false}
                                                                        tooltipText={TOOLTIPS.VIEW}
                                                                        icon={<IconEye />}
                                                                        onClick={() =>
                                                                            onView!(element.id, element.roleId)
                                                                        }
                                                                    />
                                                                </Show>
                                                                <Show when={onEdit}>
                                                                    <DisableableIconButtonWithTooltip
                                                                        disabled={false}
                                                                        tooltipText={TOOLTIPS.EDIT}
                                                                        icon={<IconPencil />}
                                                                        onClick={() => onEdit!(element.id)}
                                                                    />
                                                                </Show>
                                                                <Show when={onDelete}>
                                                                    <DisableableIconButtonWithTooltip
                                                                        disabled={false}
                                                                        tooltipText={TOOLTIPS.DELETE}
                                                                        icon={<IconTrash />}
                                                                        onClick={() => onDelete!(element.id)}
                                                                    />
                                                                </Show>
                                                            </Group>
                                                        </Group>
                                                        {element.description && (
                                                            <Controlled_RichText
                                                                readonly={true}
                                                                value={element.description}
                                                            />
                                                        )}
                                                    </Stack>
                                                </Card.Section>
                                            </Card>
                                        ))}
                                    </Stack>
                                </Card.Section>
                            </Card>
                        :   <>
                                {group.values.map((element, index) => (
                                    <Card withBorder radius={'md'} key={index}>
                                        <Card.Section withBorder inheritPadding py="xs">
                                            <Stack align={'flex-start'} justify={'flex-start'} w={'100%'}>
                                                <Group w={'100%'}>
                                                    {element.img && (
                                                        <img
                                                            alt={element.img}
                                                            src={element.img}
                                                            style={{ height: '30px' }}
                                                        />
                                                    )}
                                                    <Text size={'lg'}>{element.text}</Text>
                                                    <div style={{ flexGrow: 1 }}></div>
                                                    <Group justify={'flex-end'}>
                                                        <Show when={onView}>
                                                            <DisableableIconButtonWithTooltip
                                                                disabled={false}
                                                                tooltipText={TOOLTIPS.VIEW}
                                                                icon={<IconEye />}
                                                                onClick={() => onView!(element.id, element.roleId)}
                                                            />
                                                        </Show>
                                                        <Show when={onEdit}>
                                                            <DisableableIconButtonWithTooltip
                                                                disabled={false}
                                                                tooltipText={TOOLTIPS.EDIT}
                                                                icon={<IconPencil />}
                                                                onClick={() => onEdit!(element.id)}
                                                            />
                                                        </Show>
                                                        <Show when={onDelete}>
                                                            <DisableableIconButtonWithTooltip
                                                                disabled={false}
                                                                tooltipText={TOOLTIPS.DELETE}
                                                                icon={<IconTrash />}
                                                                onClick={() => onDelete!(element.id)}
                                                            />
                                                        </Show>
                                                    </Group>
                                                </Group>
                                                {element.description && (
                                                    <Controlled_RichText readonly={true} value={element.description} />
                                                )}
                                            </Stack>
                                        </Card.Section>
                                    </Card>
                                ))}
                            </>
                    )}
                </Stack>
            </div>
        </div>
    );
};
